import React, {useState} from 'react';
import {
    DateCalendar,
    DayCalendarSkeleton,
    LocalizationProvider,
    PickersDay,
    PickersDayProps
} from "@mui/x-date-pickers";
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, {Dayjs} from "dayjs";
import 'dayjs/locale/cs';
import {Race} from "../../../schemas/races/racesSchema";
import {Box, IconButton, Stack, Tooltip, tooltipClasses, TooltipProps, Typography} from "@mui/material";
import {styled} from "@mui/material/styles";
import {InfoOutlined} from "@mui/icons-material";

interface RaceCalendarProps {
    races: Race[] | undefined;
}

const HtmlTooltip = styled(({className, ...props}: TooltipProps) => (
    <Tooltip {...props} classes={{popper: className}}/>
))(({theme}) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: '#f9f9fd',
        color: 'rgba(0, 0, 0, 0.87)',
        fontSize: theme.typography.pxToRem(12),
        border: '1px solid #dadde9',
    },
}));

const RacesCalendar: React.FC<RaceCalendarProps> = ({races}) => {
    const [currentMonth, setCurrentMonth] = useState(dayjs().month());
    const highlightedDays = races?.filter(race => dayjs(race.date).month() === currentMonth)
        .map(race => dayjs(race.date).date()) || [];

    const RaceDay = (props: PickersDayProps<Dayjs> & { highlightedDays?: number[] }) => {
        const {highlightedDays = [], day, outsideCurrentMonth} = props;

        const isRace = !outsideCurrentMonth && highlightedDays.includes(day.date());
        const isToday = dayjs().isSame(day, 'day');

        const race = races?.find(race => dayjs(race.date).isSame(day, 'day'));
        const title = race ? (
            <Box>
                <Stack direction="column">
                    <Typography variant="subtitle1" component="div" sx={{fontWeight: 'bold'}}>
                        Závod: {race.name}
                    </Typography>
                    <Typography variant="body2">
                        Datum: {dayjs(race.date).format('DD.MM.YYYY')}
                    </Typography>
                    {isToday && (
                        <Typography variant="body2" sx={{color: 'red'}}>
                            Dnes
                        </Typography>
                    )}
                    {race.publishStatus === "PUBLISHED" && (
                        <IconButton sx={{ml: 'auto', color: 'primary.main'}} title="Více informací" onClick={
                            () => document.getElementById(`race-${race.id}`)?.scrollIntoView({
                                behavior: 'smooth',
                                block: 'start',
                            })
                        }>
                            <InfoOutlined/>
                        </IconButton>
                    )}
                </Stack>
            </Box>
        ) : (
            isToday && <Typography variant="body2">Dnes</Typography>
        );

        return (
            <HtmlTooltip title={title} placement="top" arrow>
                <PickersDay
                    {...props}
                    sx={{
                        ...(isRace && {
                            backgroundColor: 'primary.main',
                            color: 'white',
                            '&:hover': {
                                backgroundColor: 'primary.dark',
                            },
                        }),
                        ...(isToday && {
                            fontWeight: 'bold',
                        }),
                    }}
                />
            </HtmlTooltip>
        );
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="cs">
            <DateCalendar
                readOnly
                views={['day']}
                showDaysOutsideCurrentMonth
                loading={!races}
                renderLoading={() => <DayCalendarSkeleton/>}
                onMonthChange={(date) => setCurrentMonth(date.month())}
                slots={{
                    day: RaceDay,
                }}
                slotProps={{
                    day: {
                        highlightedDays
                    } as any,
                }}
                sx={{
                    width: "100%",
                    height: "80%",
                    my: -2
                }}
            />
        </LocalizationProvider>
    );
};

export default RacesCalendar;